import React, { useState, useEffect } from 'react';
import { loadsApi, unitsApi, brokersApi, summaryApi, amazonRelayApi, companyApi, unitApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SmartNotes from '../components/SmartNotes';
import { DragDropZone } from '../components/DragDropZone';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AdditionsPanel from '../components/AdditionsPanel';
import UnitSelectItems from '../components/UnitSelectItems';
import WeekJumper from '../components/WeekJumper';
import DateRangePicker from '../components/DateRangePicker';
import BrokerDocImportDialog from '../components/BrokerDocImportDialog';
import { 
  Package, 
  Plus, 
  Pencil, 
  Trash2,
  ChevronLeft,
  ChevronRight,
  Calendar,
  CalendarRange,
  MapPin,
  Building2,
  FileText,
  Download,
  X,
  ChevronDown,
  Upload,
  Search,
  Sparkles,
  Loader2,
  CheckCircle,
  Check,
  AlertCircle,
  User,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, getWeekStart, getWeekRange, formatDate, apiErrorMessage, addDaysYMD } from '../lib/utils';
import { StatTile } from '../components/dashboard/KpiStrip';
import { TripImportHelp } from '../components/loads/TripImportHelp';
import { PaymentFilesPicker } from '../components/loads/PaymentFilesPicker';
import { TripKindFilter, PaidBadge } from '../components/loads/TripKindFilter';

/**
 * Searchable broker selector with sticky "Add New Broker" at the top.
 * Used inside the Add Load form so users with many brokers can find one quickly.
 */
function BrokerSelector({ brokers, value, onChange, onAddNew, showAmazon = true }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const wrapperRef = React.useRef(null);

  React.useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // "Amazon" first (when enabled), then everything else, deduped
  const allBrokers = React.useMemo(() => {
    const others = (brokers || []).filter((b) => b.name !== 'Amazon');
    return showAmazon ? [{ id: '_amazon_', name: 'Amazon' }, ...others] : others;
  }, [brokers, showAmazon]);

  const filtered = React.useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return allBrokers;
    return allBrokers.filter((b) => b.name.toLowerCase().includes(s));
  }, [allBrokers, search]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm hover:bg-accent/30"
        data-testid="broker-select"
      >
        <span className={value ? '' : 'text-muted-foreground'}>{value || 'Select broker'}</span>
        <svg className="h-4 w-4 opacity-50" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-input bg-popover shadow-lg" data-testid="broker-dropdown">
          {/* Sticky Add New + Search at top */}
          <div className="sticky top-0 bg-popover border-b p-2 space-y-2">
            <button
              type="button"
              onClick={() => { setOpen(false); onAddNew(); }}
              className="w-full flex items-center gap-2 text-sm font-semibold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded px-2 py-1.5 border border-dashed border-blue-300 dark:border-blue-700"
              data-testid="broker-add-new"
            >
              <Plus className="h-4 w-4" />Add New Broker
            </button>
            <div className="relative">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search brokers…"
                className="h-8 w-full rounded border border-input bg-background pl-7 pr-2 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="broker-search-input"
              />
              <svg className="h-3 w-3 absolute left-2 top-2.5 opacity-50" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 3a6 6 0 100 12 6 6 0 000-12zm-8 6a8 8 0 1114.32 4.906l4.387 4.387a1 1 0 01-1.414 1.414l-4.387-4.387A8 8 0 011 9z" clipRule="evenodd" /></svg>
            </div>
          </div>
          {/* Scrollable broker list */}
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-muted-foreground italic">No brokers match &quot;{search}&quot;</li>
            ) : filtered.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => { onChange(b.name); setOpen(false); setSearch(''); }}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-accent ${value === b.name ? 'bg-blue-50 dark:bg-blue-950/30 font-semibold' : ''}`}
                  data-testid={`broker-option-${b.name}`}
                >
                  {b.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Loads() {
  const { canEdit } = useAuth();
  const [loads, setLoads] = useState([]);
  const [units, setUnits] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState('all');
  const [weekStart, setWeekStart] = useState(getWeekStart());
  // Date filter mode: 'week' (single pay week) OR 'range' (custom start→end)
  const [dateMode, setDateMode] = useState('week');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLoad, setEditingLoad] = useState(null);
  // Duplicate-load-across-weeks confirm dialog
  const [dupWarning, setDupWarning] = useState(null); // { existing, incoming: formData }
  const [useCustomBroker, setUseCustomBroker] = useState(false);
  const [newBrokerName, setNewBrokerName] = useState('');
  const [addingBroker, setAddingBroker] = useState(false);
  // Company-level: when false, the Amazon-default toggle is hidden and every
  // new load uses the Custom broker picker.
  const [companyDefaultBrokerEnabled, setCompanyDefaultBrokerEnabled] = useState(true);
  const [companyDefaultBrokerName, setCompanyDefaultBrokerName] = useState('Amazon');
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  
  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfUnitNumber, setPdfUnitNumber] = useState('');
  
  // Date Range Invoice state
  const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
  const [invoiceStartDate, setInvoiceStartDate] = useState('');
  const [invoiceEndDate, setInvoiceEndDate] = useState('');
  
  // Amazon Relay Import state
  const [relayDialogOpen, setRelayDialogOpen] = useState(false);
  const [relayStep, setRelayStep] = useState(1); // 1=upload, 2=review
  const [relayTripsFile, setRelayTripsFile] = useState(null);
  const [relayPaymentFile, setRelayPaymentFile] = useState(null);
  const [relayProcessing, setRelayProcessing] = useState(false);
  const [relayResults, setRelayResults] = useState(null);
  const [relayApplying, setRelayApplying] = useState(false);
  const [relayOverrides, setRelayOverrides] = useState({}); // load_ref -> {unit_id, driver_id}

  // Amazon Trip History (XLSX) Import state
  const [tripImportOpen, setTripImportOpen] = useState(false);
  const [brokerDocImportOpen, setBrokerDocImportOpen] = useState(false);
  const [tripImportFile, setTripImportFile] = useState(null);
  const [tripPayFiles, setTripPayFiles] = useState({ spot: null, contract: null });
  const [tripKindFilter, setTripKindFilter] = useState('both');
  const [tripImporting, setTripImporting] = useState(false);
  const [tripImportResult, setTripImportResult] = useState(null);
  const [tripImportIgnoreCutoff, setTripImportIgnoreCutoff] = useState(false);
  // Per-block unit overrides set by the user in the preview table:  { block_id: unit_id }
  const [tripImportOverrides, setTripImportOverrides] = useState({});
  // Per-trip total-payout overrides — for trips whose CSV row has no Estimated Cost.
  // Keyed by Trip ID (the bucket id). Values are numeric strings while editing.
  const [tripImportPayoutOverrides, setTripImportPayoutOverrides] = useState({});
  // Force-include: block/trip IDs the dispatcher explicitly opted-in to (skipped rows
  // that they want to import anyway — e.g. duplicates, outside the week, or missing pay).
  const [tripImportForceInclude, setTripImportForceInclude] = useState(new Set());
  // OPT-OUT: block/trip IDs the dispatcher explicitly UN-checked in the preview
  // even though they would normally be imported. Rows in this set are dropped
  // from the commit.
  const [tripImportExclude, setTripImportExclude] = useState(new Set());

  // Bulk-delete selection state
  const [selectedLoadIds, setSelectedLoadIds] = useState(new Set());
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Search query (filters loads in the table by reference / route / broker / notes)
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    load_reference: '',
    broker_name: 'Amazon',
    pickup_location: '',
    delivery_location: '',
    start_date: '',
    end_date: '',
    payout_amount: '',
    miles: '',
    fee_type: 'percentage',
    fee_value: '10',
    load_type: '',
    notes: '',
    status: 'completed',
    unit_id: '',
  });

  useEffect(() => {
    loadUnits();
    loadBrokers();
    loadCompanyDefaults();
  }, []);

  useEffect(() => {
    loadLoads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, selectedUnit, dateMode, rangeStart, rangeEnd]);

  const loadCompanyDefaults = async () => {
    try {
      const res = await companyApi.get();
      const useDefault = !!res.data?.use_default_broker;
      const defaultName = res.data?.default_broker || 'Amazon';
      setCompanyDefaultBrokerEnabled(useDefault);
      setCompanyDefaultBrokerName(defaultName);
      if (useDefault) {
        setFormData((p) => ({ ...p, broker_name: defaultName }));
      } else {
        // Force the custom-broker picker; clear the pre-fill.
        setUseCustomBroker(true);
        setFormData((p) => ({ ...p, broker_name: '' }));
      }
    } catch { /* noop */ }
  };

  // Unit-info card data (driver, owner-op, dispatcher of the selected unit)
  const [unitPeople, setUnitPeople] = useState(null);
  // All people for the override dropdowns
  const [allDrivers, setAllDrivers] = useState([]);
  const [allDispatchers, setAllDispatchers] = useState([]);
  const [allOwnerOps, setAllOwnerOps] = useState([]);
  // Per-load override selections (null = use unit default)
  const [overrideDriverId, setOverrideDriverId] = useState(null);
  const [overrideDispatcherId, setOverrideDispatcherId] = useState(null);
  const [overrideOwnerOpId, setOverrideOwnerOpId] = useState(null);
  // "How to apply?" prompt when user changes a field
  const [reassignPrompt, setReassignPrompt] = useState(null); // { kind, oldId, newId, name } | null

  useEffect(() => {
    if (formData.unit_id) {
      unitApi.getPeople(formData.unit_id).then((r) => {
        setUnitPeople(r.data);
        // Reset overrides whenever unit changes
        setOverrideDriverId(r.data.driver?.id || null);
        setOverrideDispatcherId(r.data.dispatchers?.[0]?.id || null);
        setOverrideOwnerOpId(r.data.owner_operators?.[0]?.id || null);
      }).catch(() => setUnitPeople(null));
    } else {
      setUnitPeople(null);
      setOverrideDriverId(null);
      setOverrideDispatcherId(null);
      setOverrideOwnerOpId(null);
    }
  }, [formData.unit_id]);

  // Load lookups once
  useEffect(() => {
    import('../lib/api').then(({ driversApi, dispatchersApi, ownerOperatorsApi }) => {
      driversApi.getAll().then((r) => setAllDrivers(r.data || [])).catch(() => {});
      dispatchersApi.getAll().then((r) => setAllDispatchers(r.data || [])).catch(() => {});
      ownerOperatorsApi.getAll().then((r) => setAllOwnerOps(r.data || [])).catch(() => {});
    });
  }, []);

  // When the user changes one of the dropdowns, show the prompt
  const askReassign = (kind, oldId, newId, list) => {
    if (oldId === newId) return;
    const name = list.find((p) => p.id === newId)?.name || '—';
    setReassignPrompt({ kind, oldId, newId, name });
  };

  const applyOverrideForLoadOnly = () => {
    if (!reassignPrompt) return;
    if (reassignPrompt.kind === 'driver') setOverrideDriverId(reassignPrompt.newId);
    if (reassignPrompt.kind === 'dispatcher') setOverrideDispatcherId(reassignPrompt.newId);
    if (reassignPrompt.kind === 'owner_op') setOverrideOwnerOpId(reassignPrompt.newId);
    setReassignPrompt(null);
    toast.success('Override saved — this load only');
  };

  const applyReassignToUnit = async () => {
    if (!reassignPrompt || !formData.unit_id) return;
    try {
      // Fetch the unit, update the assigned field, save
      const cur = units.find((u) => u.id === formData.unit_id);
      const patch = { ...cur };
      if (reassignPrompt.kind === 'driver') patch.assigned_driver_id = reassignPrompt.newId;
      if (reassignPrompt.kind === 'dispatcher') patch.assigned_dispatcher_id = reassignPrompt.newId;
      if (reassignPrompt.kind === 'owner_op') patch.assigned_owner_operator_id = reassignPrompt.newId;
      // Strip read-only fields
      ['id', 'company_id', 'created_at', 'assigned_driver_name'].forEach((k) => delete patch[k]);
      await unitsApi.update(formData.unit_id, patch);
      // Refresh local card
      const r = await unitApi.getPeople(formData.unit_id);
      setUnitPeople(r.data);
      // Sync overrides
      if (reassignPrompt.kind === 'driver') setOverrideDriverId(reassignPrompt.newId);
      if (reassignPrompt.kind === 'dispatcher') setOverrideDispatcherId(reassignPrompt.newId);
      if (reassignPrompt.kind === 'owner_op') setOverrideOwnerOpId(reassignPrompt.newId);
      setReassignPrompt(null);
      toast.success(`Unit permanently re-assigned`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-assign');
    }
  };

  const loadUnits = async () => {
    try {
      const res = await unitsApi.getAll();
      setUnits(res.data);
    } catch (error) {
      toast.error('Failed to load units');
    }
  };

  const loadBrokers = async () => {
    try {
      const res = await brokersApi.getAll();
      setBrokers(res.data);
    } catch (error) {
      console.error('Failed to load brokers:', error);
    }
  };

  const loadLoads = async () => {
    // In range mode, wait for both dates before fetching
    if (dateMode === 'range' && (!rangeStart || !rangeEnd)) return;

    setLoading(true);
    try {
      const params = {};
      if (dateMode === 'range') {
        params.start_date = rangeStart;
        params.end_date = rangeEnd;
      } else {
        params.week_start = weekStart;
      }
      if (selectedUnit !== 'all') {
        params.unit_id = selectedUnit;
      }
      const res = await loadsApi.getAll(params);
      setLoads(res.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // NOTE: loads STAY in whichever pay week they were originally created for.
      // Editing the pickup/start date will NOT move the load to a different
      // week — that behaviour was reverted (Feb 2026) because it caused loads
      // to jump between weeks unexpectedly. If a load truly belongs in a
      // different week, delete it and re-create it in that week.
      const data = {
        ...formData,
        payout_amount: parseFloat(formData.payout_amount),
        miles: formData.miles ? parseFloat(formData.miles) : null,
        fee_value: parseFloat(formData.fee_value),
        // On CREATE: use the currently-viewed pay week.
        // On UPDATE: keep the load's original week_start (do not move it).
        week_start: editingLoad ? editingLoad.week_start : weekStart,
      };

      if (editingLoad) {
        await loadsApi.update(editingLoad.id, data);
        toast.success('Load updated successfully');
      } else {
        try {
          await loadsApi.create(data);
          toast.success('Load created successfully');
        } catch (err) {
          const d = err?.response?.data?.detail;
          if (err?.response?.status === 409 && d && typeof d === 'object' && d.code === 'duplicate_load_reference') {
            // Show themed confirm dialog — DON'T close the form so user can adjust if they cancel.
            setDupWarning({ existing: d.existing, incoming: data });
            return;
          }
          throw err;
        }
      }
      setDialogOpen(false);
      resetForm();
      loadLoads();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Operation failed'));
    }
  };

  // User confirmed they want to create the duplicate on purpose
  const confirmDupCreate = async () => {
    const data = dupWarning?.incoming;
    if (!data) return;
    try {
      await loadsApi.create({ ...data }, { params: { force: true } });
      toast.success('Duplicate load created');
      setDupWarning(null);
      setDialogOpen(false);
      resetForm();
      loadLoads();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to create'));
    }
  };

  const handleEdit = (load) => {
    setEditingLoad(load);
    // Reflect the actual broker on the toggle. If the company has the
    // "default broker" feature turned off, every load edits under Custom.
    const isDefaultBroker = companyDefaultBrokerEnabled &&
      (load.broker_name || '').trim().toLowerCase() === (companyDefaultBrokerName || 'Amazon').trim().toLowerCase();
    setUseCustomBroker(!isDefaultBroker);
    setAddingBroker(false);
    setNewBrokerName('');
    setFormData({
      load_reference: load.load_reference,
      broker_name: load.broker_name,
      pickup_location: load.pickup_location,
      delivery_location: load.delivery_location,
      start_date: load.start_date,
      end_date: load.end_date,
      payout_amount: load.payout_amount.toString(),
      miles: load.miles ? load.miles.toString() : '',
      fee_type: load.fee_type,
      fee_value: load.fee_value.toString(),
      load_type: load.load_type || '',
      notes: load.notes || '',
      status: load.status,
      unit_id: load.unit_id,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this load?')) return;
    try {
      await loadsApi.delete(id);
      toast.success('Load deleted successfully');
      loadLoads();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Delete failed'));
    }
  };

  const resetForm = () => {
    setEditingLoad(null);
    // Respect the company "default broker" toggle:
    //   • enabled  → default row uses the configured broker (usually Amazon),
    //                the Amazon/Custom switch starts on the default side.
    //   • disabled → toggle is hidden, every new load starts on Custom.
    setUseCustomBroker(!companyDefaultBrokerEnabled);
    setNewBrokerName('');
    setAddingBroker(false);
    setFormData({
      load_reference: '',
      broker_name: companyDefaultBrokerEnabled ? (companyDefaultBrokerName || 'Amazon') : '',
      pickup_location: '',
      delivery_location: '',
      start_date: '',
      end_date: '',
      payout_amount: '',
      miles: '',
      fee_type: 'percentage',
      fee_value: '10',
      load_type: '',
      notes: '',
      status: 'completed',
      unit_id: '',
    });
  };

  const handleAddBroker = async () => {
    if (!newBrokerName.trim()) {
      toast.error('Please enter a broker name');
      return;
    }
    try {
      const res = await brokersApi.create({ name: newBrokerName.trim() });
      toast.success('Broker added successfully');
      await loadBrokers();
      setFormData({ ...formData, broker_name: res.data.name });
      setNewBrokerName('');
      setAddingBroker(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add broker');
    }
  };

  const handleGenerateInvoice = async () => {
    if (selectedUnit === 'all') {
      toast.error('Please select a specific unit to generate invoice');
      return;
    }
    if (filteredLoads.length === 0) {
      toast.error('No loads to generate invoice for');
      return;
    }
    
    setGeneratingInvoice(true);
    try {
      // Use the same Settlement Pay PDF as Summary page
      const res = await summaryApi.exportPdf(selectedUnit, weekStart);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in PDF viewer (same as Summary page)
      setPdfUrl(url);
      setPdfUnitNumber(getUnitNumber(selectedUnit));
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to generate PDF'));
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const openDateRangeDialog = () => {
    if (selectedUnit === 'all') {
      toast.error('Please select a specific unit first');
      return;
    }
    // Default to current month
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    setInvoiceStartDate(firstDayOfMonth.toISOString().split('T')[0]);
    setInvoiceEndDate(today.toISOString().split('T')[0]);
    setDateRangeDialogOpen(true);
  };

  const handleGenerateCustomInvoice = async () => {
    if (!invoiceStartDate || !invoiceEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    if (invoiceStartDate > invoiceEndDate) {
      toast.error('Start date must be before end date');
      return;
    }
    
    setGeneratingInvoice(true);
    setDateRangeDialogOpen(false);
    try {
      const res = await summaryApi.generateInvoiceCustom(selectedUnit, invoiceStartDate, invoiceEndDate);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      setPdfUrl(url);
      setPdfUnitNumber(getUnitNumber(selectedUnit));
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to generate invoice'));
    } finally {
      setGeneratingInvoice(false);
    }
  };
  
  // Download PDF from viewer
  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `Settlement_Pay_${pdfUnitNumber}_${weekStart}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('PDF downloaded');
    }
  };
  
  // Close PDF viewer and cleanup
  const closePdfViewer = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfViewerOpen(false);
    setPdfUrl(null);
    setPdfUnitNumber('');
  };

  const navigateWeek = (direction) => {
    setWeekStart(addDaysYMD(weekStart, direction * 7));
  };

  // The "import week" is the week BEFORE the currently selected week.
  // Rationale: dispatchers enter last week's loads in the current pay week, so the
  // imported trips belong to the previous Monday-Sunday range.
  const importWeekStart = addDaysYMD(weekStart, -7);

  // ---- Amazon Trip History Import Handler (two-step: preview -> confirm) ----
  const handleTripImport = async (commit = false, opts = {}) => {
    if (!tripImportFile) {
      toast.error('Please choose a file');
      return;
    }
    setTripImporting(true);
    try {
      const fd = new FormData();
      // The PAY week (where loads will be saved) — the week the user is currently viewing.
      fd.append('week_start', weekStart);
      // The SOURCE week (whose Sunday-noon cutoff applies to file rows) — the previous
      // week, since the dispatcher is entering last week's loads in this pay week.
      fd.append('source_week_start', importWeekStart);
      fd.append('ignore_cutoff', tripImportIgnoreCutoff ? 'true' : 'false');
      fd.append('dry_run', commit ? 'false' : 'true');
      // When user OKs the cross-week duplicate dialog we retry with this flag.
      if (opts.forceCrossWeek) fd.append('force_cross_week', 'true');
      // Send per-block unit overrides (only meaningful on commit, but harmless on preview re-runs)
      if (commit && Object.keys(tripImportOverrides).length > 0) {
        fd.append('overrides', JSON.stringify(tripImportOverrides));
      }
      // Per-trip payout overrides — apply on BOTH preview re-runs and commit so the
      // entered amount stays visible while the user is still in the dialog.
      const cleanedPayouts = Object.fromEntries(
        Object.entries(tripImportPayoutOverrides)
          .map(([k, v]) => [k, parseFloat(v)])
          .filter(([, v]) => !isNaN(v) && v > 0)
      );
      if (Object.keys(cleanedPayouts).length > 0) {
        fd.append('payout_overrides', JSON.stringify(cleanedPayouts));
      }
      // Force-include: block/trip IDs the user explicitly opted into (skipped rows).
      if (tripImportForceInclude.size > 0) {
        fd.append('force_include', JSON.stringify(Array.from(tripImportForceInclude)));
      }
      // Exclude any rows the user UN-checked in the preview.
      if (tripImportExclude.size > 0) {
        fd.append('exclude', JSON.stringify(Array.from(tripImportExclude)));
      }
      fd.append('file', tripImportFile);
      if (tripPayFiles.spot) fd.append('spot_payment_file', tripPayFiles.spot);
      if (tripPayFiles.contract) fd.append('contract_payment_file', tripPayFiles.contract);
      const res = await api.post('/loads/import-amazon-trips', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTripImportResult(res.data);
      if (commit) {
        // If backend returned cross-week duplicates AND we didn't already force,
        // pause the flow and open the themed confirm dialog. The Amazon import
        // dialog stays open so the dispatcher can compare.
        const xdups = res.data.cross_week_duplicates || [];
        if (xdups.length > 0 && !opts.forceCrossWeek) {
          const nonDupCreated = res.data.created_count + res.data.created_unassigned_count;
          if (nonDupCreated > 0) {
            toast.success(`Imported ${nonDupCreated} load(s). Please review ${xdups.length} cross-week match(es).`);
            loadLoads();
          }
          setTripImportCrossWeekDups(xdups);
          return;
        }
        const total = res.data.created_count + res.data.created_unassigned_count;
        if (total > 0) {
          const parts = [];
          if (res.data.created_count > 0) parts.push(`${res.data.created_count} assigned`);
          if (res.data.created_unassigned_count > 0) parts.push(`${res.data.created_unassigned_count} unassigned`);
          toast.success(`Imported ${total} load${total !== 1 ? 's' : ''} (${parts.join(', ')})`);
          loadLoads(); // refresh main list
          setTripImportOpen(false);
        } else {
          toast.info('No loads imported');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import trips');
    } finally {
      setTripImporting(false);
    }
  };

  // Reset import dialog state on close
  const closeTripImportDialog = () => {
    setTripImportOpen(false);
    // Defer state reset so the closing animation looks clean
    setTimeout(() => {
      setTripImportFile(null);
      setTripPayFiles({ spot: null, contract: null });
      setTripKindFilter('both');
      setTripImportResult(null);
      setTripImportIgnoreCutoff(false);
      setTripImportOverrides({});
      setTripImportPayoutOverrides({});
      setTripImportForceInclude(new Set());
      setTripImportExclude(new Set());
    }, 200);
  };

  // ---- Bulk select / delete ----
  const toggleSelectLoad = (id) => {
    setSelectedLoadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedLoadIds.size === filteredLoads.length && filteredLoads.length > 0) {
      setSelectedLoadIds(new Set());
    } else {
      setSelectedLoadIds(new Set(filteredLoads.map((l) => l.id)));
    }
  };
  // Clear selection when the underlying list changes (week/filter/range)
  useEffect(() => {
    setSelectedLoadIds(new Set());
  }, [weekStart, selectedUnit, dateMode, rangeStart, rangeEnd]);

  const handleBulkDelete = async () => {
    if (selectedLoadIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const res = await api.post('/loads/bulk-delete', {
        load_ids: Array.from(selectedLoadIds),
      });
      toast.success(`Deleted ${res.data.deleted_count} load${res.data.deleted_count !== 1 ? 's' : ''}`);
      setSelectedLoadIds(new Set());
      setBulkDeleteConfirmOpen(false);
      loadLoads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ---- Amazon Relay Import Handlers ----
  const handleRelayProcess = async () => {
    if (!relayTripsFile || !relayPaymentFile) {
      toast.error('Please upload both a Trips file and a Payment file');
      return;
    }
    setRelayProcessing(true);
    try {
      const res = await amazonRelayApi.process(relayTripsFile, relayPaymentFile, weekStart);
      setRelayResults(res.data);
      setRelayStep(2);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to process Amazon Relay files'));
    } finally {
      setRelayProcessing(false);
    }
  };

  // Cross-week duplicate confirm state (set when Amazon apply returns dups)
  const [relayCrossWeekDups, setRelayCrossWeekDups] = useState(null); // { dups, entries }
  const [tripImportCrossWeekDups, setTripImportCrossWeekDups] = useState(null);

  const handleRelayApply = async (opts = {}) => {
    if (!relayResults) return;
    const { forceCrossWeek = false, skipRefs = [] } = opts;
    setRelayApplying(true);
    try {
      const entries = relayResults.loads
        .filter(l => !l.is_duplicate)
        .map(l => {
          const override = relayOverrides[l.load_reference] || {};
          const unitId = override.unit_id || l.unit_id;
          return {
            unit_id: unitId,
            load_reference: l.load_reference,
            load_type: l.load_type,
            block_id: l.block_id,
            payout_amount: l.payout_amount,
            distance: l.distance,
            pickup: l.pickup,
            delivery: l.delivery,
            start_date: l.start_date,
            end_date: l.end_date,
          };
        })
        .filter(e => e.unit_id);

      if (entries.length === 0) {
        toast.error('No valid entries to import. Please assign units to all loads.');
        setRelayApplying(false);
        return;
      }

      const res = await amazonRelayApi.apply(entries, relayResults.week_start, {
        forceCrossWeek, skipRefs,
      });

      // If backend detected cross-week duplicates AND we didn't already force,
      // stash them and open the confirm dialog. The Amazon import dialog stays
      // open so the user can compare.
      const dups = res.data.cross_week_duplicates || [];
      if (dups.length > 0 && !forceCrossWeek) {
        setRelayCrossWeekDups({ dups, entries });
        if (res.data.created > 0) {
          toast.success(`Created ${res.data.created} non-duplicate load(s). Please review ${dups.length} cross-week match(es).`);
        }
        setRelayApplying(false);
        return;
      }

      setRelayDialogOpen(false);
      resetRelayState();
      toast.success(`Amazon Relay import complete! Created ${res.data.created} loads, skipped ${res.data.skipped} duplicates.`);
      loadLoads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import loads');
    } finally {
      setRelayApplying(false);
    }
  };

  const resetRelayState = () => {
    setRelayStep(1);
    setRelayTripsFile(null);
    setRelayPaymentFile(null);
    setRelayResults(null);
    setRelayOverrides({});
  };

  const handleRelayDriverChoice = (loadRef, driverName) => {
    // Find the matching system driver and their unit
    const allDrivers = relayResults?.all_drivers || [];
    const matched = allDrivers.find(d => {
      const dLower = d.name.toLowerCase();
      const nLower = driverName.toLowerCase();
      return dLower === nLower || dLower.includes(nLower) || nLower.includes(dLower) ||
        (dLower.split(' ').pop() === nLower.split(' ').pop());
    });
    if (matched && matched.assigned_unit_id && matched.assigned_unit_id !== 'none') {
      setRelayOverrides(prev => ({
        ...prev,
        [loadRef]: { unit_id: matched.assigned_unit_id, driver_id: matched.id }
      }));
    } else {
      // No unit, let them also pick a unit
      setRelayOverrides(prev => ({
        ...prev,
        [loadRef]: { ...prev[loadRef], driver_id: matched?.id || null }
      }));
    }
  };

  const handleRelayUnitOverride = (loadRef, unitId) => {
    setRelayOverrides(prev => ({
      ...prev,
      [loadRef]: { ...prev[loadRef], unit_id: unitId }
    }));
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-red-100 text-red-700',
  };

  const getUnitNumber = (unitId) => {
    if (!unitId) return null;
    if (unitId === 'COMPANY') return 'Company';
    const unit = units.find(u => u.id === unitId);
    return unit?.unit_number || 'Unknown';
  };

  // Inline assign-unit handler for unassigned imported loads
  const handleAssignUnit = async (loadId, unitId) => {
    try {
      await api.patch(`/loads/${loadId}/assign-unit`, { unit_id: unitId });
      toast.success('Unit assigned');
      loadLoads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign unit');
    }
  };

  // Filter loads by selected unit + search query
  const filteredLoads = (() => {
    let list = selectedUnit === 'all'
      ? loads
      : loads.filter(load => load.unit_id === selectedUnit);
    if (searchQuery) {
      const q = searchQuery.toLowerCase().trim();
      // Strip non-digit/decimal so "$1,200" or "1200" both match an amount
      const cleanQ = q.replace(/[^0-9.]/g, '');
      const amountMatches = (n) => {
        if (!cleanQ || n == null) return false;
        const s = String(n);
        const fixed = Number(n).toFixed(2);
        return s.includes(cleanQ) || fixed.includes(cleanQ);
      };
      list = list.filter((l) => (
        (l.load_reference || '').toLowerCase().includes(q) ||
        (l.broker_name || '').toLowerCase().includes(q) ||
        (l.pickup_location || '').toLowerCase().includes(q) ||
        (l.delivery_location || '').toLowerCase().includes(q) ||
        (l.notes || '').toLowerCase().includes(q) ||
        (getUnitNumber(l.unit_id) || '').toLowerCase().includes(q) ||
        // Amount fields — user can now search by payout, dispatch fee, or net
        amountMatches(l.payout_amount) ||
        amountMatches(l.dispatch_fee)
      ));
    }
    return list;
  })();

  // Calculate week totals
  const weekTotals = filteredLoads.reduce((acc, load) => ({
    gross: acc.gross + load.payout_amount,
    fees: acc.fees + load.dispatch_fee,
  }), { gross: 0, fees: 0 });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="loads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="dash-rise">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Dispatch</p>
          <h1 className="text-3xl sm:text-4xl font-manrope font-bold tracking-tight text-slate-900 dark:text-white mt-1">Weekly Loads</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-1">Manage dispatch loads by week</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={generatingInvoice || selectedUnit === 'all'}
                data-testid="generate-invoice-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onClick={handleGenerateInvoice}
                disabled={filteredLoads.length === 0}
              >
                <Calendar className="h-4 w-4 mr-2" />
                This Week ({getWeekRange(weekStart)})
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={openDateRangeDialog}>
                <CalendarRange className="h-4 w-4 mr-2" />
                Custom Date Range...
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canEdit && (
            <>
            {/* Amazon Relay Import — temporarily disabled */}

            {/* Import Loads dropdown — Amazon Trip History + Broker Docs */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="import-loads-btn"
                  className="border-blue-400 text-blue-700 hover:bg-blue-50 dark:border-blue-500/40 dark:text-blue-300 dark:hover:bg-blue-950/30"
                >
                  <Upload className="h-4 w-4 mr-1 sm:mr-2" />
                  Import Loads
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem
                  onClick={() => { setTripImportOpen(true); setTripImportResult(null); setTripImportFile(null); }}
                  data-testid="import-amazon-trips-menu"
                >
                  <Package className="h-4 w-4 mr-2 text-blue-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">From Amazon Trip History</span>
                    <span className="text-[10px] text-muted-foreground">CSV export from Amazon Relay</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setBrokerDocImportOpen(true)}
                  data-testid="import-broker-docs-menu"
                >
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">From Broker Documents</span>
                    <span className="text-[10px] text-muted-foreground">Rate Con / BOL PDFs · powered by AI</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Add Load Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-load-btn">
                  <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                  Add Load
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope">
                  {editingLoad ? 'Edit Load' : 'Add New Load'}
                </DialogTitle>
                <DialogDescription>
                  {editingLoad ? 'Update load details' : `Add a new load for week of ${getWeekRange(weekStart)}`}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="unit_id">Unit *</Label>
                    <Select
                      value={formData.unit_id}
                      onValueChange={(value) => {
                        // Auto-sync fee_type/fee_value from the newly selected unit
                        // so the Edit form immediately reflects what the backend will store.
                        // COMPANY pseudo-unit → fee 0. Regular unit → its per-unit dispatch fee override.
                        let next = { ...formData, unit_id: value };
                        if (value === 'COMPANY') {
                          next.fee_value = '0';
                        } else {
                          const u = units.find((x) => x.id === value);
                          if (u && u.dispatch_fee_type && u.dispatch_fee_value !== undefined && u.dispatch_fee_value !== null) {
                            next.fee_type = u.dispatch_fee_type;
                            next.fee_value = String(u.dispatch_fee_value);
                          }
                        }
                        setFormData(next);
                      }}
                      required
                    >
                      <SelectTrigger data-testid="load-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        <UnitSelectItems units={units} includeCompany hideInactive={!editingLoad} />
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="load_reference">Load Reference *</Label>
                    <Input
                      id="load_reference"
                      value={formData.load_reference}
                      onChange={(e) => setFormData({ ...formData, load_reference: e.target.value })}
                      placeholder="e.g., LD-001"
                      required
                      data-testid="load-reference-input"
                    />
                  </div>
                </div>

                {/* Unit Info Card — bigger, editable dropdowns for who's working this load */}
                {unitPeople && (
                  <div className="rounded-lg border-2 border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/30 p-4 space-y-3" data-testid="unit-people-card">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-bold tracking-wide">UNIT</div>
                        <span className="font-bold text-lg text-blue-700 dark:text-blue-300 font-mono">{unitPeople.unit_number}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {/* Driver */}
                      <div>
                        <Label className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase">Driver</Label>
                        <Select
                          value={overrideDriverId || 'none'}
                          onValueChange={(v) => askReassign('driver', unitPeople.driver?.id || null, v === 'none' ? null : v, allDrivers)}
                        >
                          <SelectTrigger className="bg-white dark:bg-slate-800 mt-0.5" data-testid="load-override-driver">
                            <SelectValue placeholder="No driver" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none"><span className="text-muted-foreground">No driver</span></SelectItem>
                            {allDrivers.filter((d) => d.status === 'active').map((d) => (
                              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Owner-Op */}
                      <div>
                        <Label className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase">Owner-Operator</Label>
                        <Select
                          value={overrideOwnerOpId || 'none'}
                          onValueChange={(v) => askReassign('owner_op', unitPeople.owner_operators?.[0]?.id || null, v === 'none' ? null : v, allOwnerOps)}
                        >
                          <SelectTrigger className="bg-white dark:bg-slate-800 mt-0.5" data-testid="load-override-owner-op">
                            <SelectValue placeholder="No owner-op" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none"><span className="text-muted-foreground">No owner-op</span></SelectItem>
                            {allOwnerOps.filter((o) => o.status === 'active' || o.id === unitPeople.owner_operators?.[0]?.id).map((o) => (
                              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Dispatcher */}
                      <div>
                        <Label className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase">Dispatcher</Label>
                        <Select
                          value={overrideDispatcherId || 'none'}
                          onValueChange={(v) => askReassign('dispatcher', unitPeople.dispatchers?.[0]?.id || null, v === 'none' ? null : v, allDispatchers)}
                        >
                          <SelectTrigger className="bg-white dark:bg-slate-800 mt-0.5" data-testid="load-override-dispatcher">
                            <SelectValue placeholder="No dispatcher" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none"><span className="text-muted-foreground">No dispatcher</span></SelectItem>
                            {allDispatchers.filter((d) => d.status === 'active').map((d) => (
                              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="broker_name">Broker / Company *</Label>
                    {companyDefaultBrokerEnabled && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          {useCustomBroker ? 'Custom' : `${companyDefaultBrokerName || 'Amazon'} (Default)`}
                        </span>
                        <Switch
                          checked={useCustomBroker}
                          onCheckedChange={(checked) => {
                            setUseCustomBroker(checked);
                            if (!checked) {
                              setFormData({ ...formData, broker_name: companyDefaultBrokerName || 'Amazon' });
                              setAddingBroker(false);
                            } else {
                              // Switching TO custom: clear the pre-filled default so the
                              // user consciously picks or types a broker name.
                              setFormData({ ...formData, broker_name: '' });
                            }
                          }}
                          data-testid="broker-toggle"
                        />
                      </div>
                    )}
                  </div>
                  
                  {companyDefaultBrokerEnabled && !useCustomBroker ? (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
                      <Building2 className="h-5 w-5 text-amber-600" />
                      <span className="font-medium text-amber-700 dark:text-amber-400">{companyDefaultBrokerName || 'Amazon'}</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {!addingBroker ? (
                        <BrokerSelector
                          brokers={brokers}
                          value={formData.broker_name}
                          onChange={(name) => setFormData({ ...formData, broker_name: name })}
                          onAddNew={() => setAddingBroker(true)}
                          showAmazon={companyDefaultBrokerEnabled}
                        />
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            value={newBrokerName}
                            onChange={(e) => setNewBrokerName(e.target.value)}
                            placeholder="Enter broker name"
                            data-testid="new-broker-input"
                          />
                          <Button 
                            type="button" 
                            size="sm" 
                            onClick={handleAddBroker}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="save-broker-btn"
                          >
                            Save
                          </Button>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setAddingBroker(false);
                              setNewBrokerName('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pickup_location">Pickup Location *</Label>
                    <Input
                      id="pickup_location"
                      value={formData.pickup_location}
                      onChange={(e) => setFormData({ ...formData, pickup_location: e.target.value })}
                      placeholder="City, State"
                      required
                      data-testid="load-pickup-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery_location">Delivery Location *</Label>
                    <Input
                      id="delivery_location"
                      value={formData.delivery_location}
                      onChange={(e) => setFormData({ ...formData, delivery_location: e.target.value })}
                      placeholder="City, State"
                      required
                      data-testid="load-delivery-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      data-testid="load-start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date *</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                      data-testid="load-end-date-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payout_amount">Payout Amount *</Label>
                    <Input
                      id="payout_amount"
                      type="number"
                      step="0.01"
                      value={formData.payout_amount}
                      onChange={(e) => setFormData({ ...formData, payout_amount: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="load-payout-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="miles">Miles</Label>
                    <Input
                      id="miles"
                      type="number"
                      step="0.1"
                      value={formData.miles}
                      onChange={(e) => setFormData({ ...formData, miles: e.target.value })}
                      placeholder="0"
                      data-testid="load-miles-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fee_type">Fee Type</Label>
                    <Select
                      value={formData.fee_type}
                      onValueChange={(value) => setFormData({ ...formData, fee_type: value })}
                    >
                      <SelectTrigger data-testid="load-fee-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                        <SelectItem value="flat">Flat Rate ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fee_value">
                      {formData.fee_type === 'percentage' ? 'Fee %' : 'Fee Amount'}
                    </Label>
                    <Input
                      id="fee_value"
                      type="number"
                      step="0.01"
                      value={formData.fee_value}
                      onChange={(e) => setFormData({ ...formData, fee_value: e.target.value })}
                      placeholder={formData.fee_type === 'percentage' ? '10' : '300'}
                      data-testid="load-fee-value-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="load_type">Load Type</Label>
                    <Input
                      id="load_type"
                      value={formData.load_type}
                      onChange={(e) => setFormData({ ...formData, load_type: e.target.value })}
                      placeholder="e.g., Dry Van, Reefer"
                      data-testid="load-type-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="load-status-select">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <SmartNotes
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes... (Math: 100+200 or 1000*10%)"
                    rows={2}
                    data-testid="load-notes-input"
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="load-submit-btn">
                    {editingLoad ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Broker Document Import Dialog (Rate Con / BOL) */}
          <BrokerDocImportDialog
            open={brokerDocImportOpen}
            onOpenChange={setBrokerDocImportOpen}
            weekStart={weekStart}
            units={units}
            onImported={loadLoads}
          />

          {/* Amazon Trip History Import Dialog */}
          <Dialog open={tripImportOpen} onOpenChange={(open) => !open && closeTripImportDialog()}>
            <DialogContent className="w-[95vw] max-w-[95vw] lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-manrope">Import Amazon Trip History</DialogTitle>
                <DialogDescription>
                  Importing trips from the previous week (<strong>{getWeekRange(importWeekStart)}</strong>) into your current pay week (<strong>{getWeekRange(weekStart)}</strong>).
                </DialogDescription>
              </DialogHeader>              {!tripImportResult && (
                <div className="space-y-4 pt-2">
                  <TripImportHelp />
                  <DragDropZone
                    onFileSelect={(f) => setTripImportFile(f)}
                    accept=".csv,.xlsx"
                    maxSize={25 * 1024 * 1024}
                    className={tripImportFile
                      ? "border-emerald-400 hover:border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/30"
                      : "border-blue-300 hover:border-blue-400 bg-blue-50/40 dark:bg-blue-950/20"}
                    data-testid="trip-import-dropzone"
                  >
                    <div className="flex flex-col items-center gap-3">
                      {tripImportFile ? (
                        <>
                          <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                            <Check className="h-6 w-6 text-emerald-600" />
                          </div>
                          <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300" data-testid="trip-import-filename">
                            {tripImportFile.name}
                          </p>
                          <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80">
                            {(tripImportFile.size / 1024).toFixed(1)} KB · ready to preview
                          </p>
                          <p className="text-[11px] text-muted-foreground">click or drop to replace</p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-blue-600" />
                          <p className="text-sm font-semibold">Drag & drop your Trip History file here</p>
                          <p className="text-xs text-muted-foreground">.csv or .xlsx, up to 25MB</p>
                        </>
                      )}
                    </div>
                  </DragDropZone>

                  <PaymentFilesPicker files={tripPayFiles} onChange={setTripPayFiles} />

                  {/* Override toggle */}
                  <label className="flex items-start gap-2 p-3 rounded-lg border border-dashed cursor-pointer hover:bg-muted/40 transition-colors" data-testid="trip-import-ignore-cutoff-label">
                    <input
                      type="checkbox"
                      checked={tripImportIgnoreCutoff}
                      onChange={(e) => setTripImportIgnoreCutoff(e.target.checked)}
                      className="mt-0.5 cursor-pointer"
                      data-testid="trip-import-ignore-cutoff"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">Import all blocks (ignore Sunday-noon cutoff)</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Force every block into pay week <strong>{getWeekRange(weekStart)}</strong>. Useful for backfilling.
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {tripImportResult && (
                <div className="space-y-3 pt-2 text-sm min-w-0" data-testid="trip-import-result">
                  {tripImportResult.dry_run && (
                    <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-300">
                      <strong>Preview only — nothing has been saved yet.</strong> Review the breakdown below and click <strong>Confirm</strong> to commit, or <strong>Close</strong> to cancel.
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200">
                      <div className="text-[10px] uppercase font-semibold text-emerald-700">Assigned</div>
                      <div className="text-2xl font-bold text-emerald-700">{tripImportResult.created_count}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-300">
                      <div className="text-[10px] uppercase font-semibold text-amber-700">Unassigned</div>
                      <div className="text-2xl font-bold text-amber-700">{tripImportResult.created_unassigned_count}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border">
                      <div className="text-[10px] uppercase font-semibold text-muted-foreground">Skipped</div>
                      <div className="text-2xl font-bold">
                        {tripImportResult.skipped.outside_week.length +
                          tripImportResult.skipped.duplicate.length +
                          tripImportResult.skipped.no_pay.length +
                          tripImportResult.skipped.no_end_datetime.length +
                          (tripImportResult.skipped.canceled?.length || 0)}
                      </div>
                    </div>
                    {/* Gross total of rows that will actually be imported (created + force-included) */}
                    {(() => {
                      const willImport = (tripImportResult.block_report || []).filter(b => {
                        if (tripImportExclude.has(b.block_id)) return false;
                        if (b.status === 'created' || b.status === 'created_unassigned') return true;
                        return b.status === 'skipped' && tripImportForceInclude.has(b.block_id);
                      });
                      const totalGross = willImport.reduce((sum, b) => {
                        // Prefer user-entered override, else file payout
                        const override = parseFloat(tripImportPayoutOverrides[b.block_id]);
                        if (!isNaN(override) && override > 0) return sum + override;
                        return sum + (Number(b.payout) || 0);
                      }, 0);
                      const grossFormatted = totalGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      // Auto-shrink the font when the number gets long so it doesn't
                      // overflow the small card on narrow screens.
                      const grossSizeCls = grossFormatted.length > 10 ? 'text-lg' : grossFormatted.length > 7 ? 'text-xl' : 'text-2xl';
                      return (
                        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-300 min-w-0" data-testid="trip-import-gross-total">
                          <div className="text-[10px] uppercase font-semibold text-blue-700 dark:text-blue-300 truncate">Total Gross</div>
                          <div className={`${grossSizeCls} font-bold font-mono text-blue-700 dark:text-blue-300 leading-tight truncate`} title={`$${grossFormatted}`}>
                            ${grossFormatted}
                          </div>
                          <div className="text-[10px] text-blue-600/70 dark:text-blue-400/70 truncate">
                            {willImport.length} of {tripImportResult.total_blocks_in_file} will be imported
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {(() => {
                    const needsPayoutCount = (tripImportResult.block_report || []).filter(b => b.needs_payout).length;
                    return (
                      <>
                        {tripImportResult.created_unassigned_count > 0 && (
                          <div className="rounded-lg border border-amber-300 bg-amber-50/40 dark:bg-amber-950/10 p-3 text-xs">
                            <strong className="text-amber-700">Action needed:</strong> {tripImportResult.created_unassigned_count} load{tripImportResult.created_unassigned_count !== 1 ? 's were' : ' was'} created without a unit because the driver name from Amazon does not match any driver in your system. Pick a unit from the dropdown in the <strong>Unit</strong> column for each row, or close and assign later.
                          </div>
                        )}
                        {needsPayoutCount > 0 && (
                          <div className="rounded-lg border border-amber-400 bg-amber-50/60 dark:bg-amber-950/20 p-3 text-xs" data-testid="trip-import-needs-payout-banner">
                            <strong className="text-amber-700">{needsPayoutCount} trip{needsPayoutCount !== 1 ? 's are' : ' is'} missing a total payout in the file.</strong> Enter the amount in the <strong>Payout</strong> column below — or leave it blank to import with $0 and update each load later from Amazon Relay.
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {(() => {
                    const rowGross = (b) => {
                      const o = parseFloat(tripImportPayoutOverrides[b.block_id]);
                      return !isNaN(o) && o > 0 ? o : (Number(b.payout) || 0);
                    };
                    const inImport = (b) => !tripImportExclude.has(b.block_id) && (b.status === 'created' || b.status === 'created_unassigned' || (b.status === 'skipped' && tripImportForceInclude.has(b.block_id)));
                    const totals = { trip: 0, block: 0 };
                    (tripImportResult.block_report || []).filter(inImport).forEach((b) => { totals[b.kind === 'trip' ? 'trip' : 'block'] += rowGross(b); });
                    const pc = tripImportResult.payment_check;
                    return (
                      <div className="space-y-2">
                        <TripKindFilter value={tripKindFilter} onChange={setTripKindFilter} totals={totals} />
                        {pc && (
                          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs" data-testid="payment-check-summary">
                            <span className="font-semibold">Payment check:</span>
                            <span className="text-emerald-700 dark:text-emerald-300">{pc.paid} paid</span>
                            <span className="text-rose-700 dark:text-rose-300">{pc.not_paid} not paid</span>
                            <span className="text-muted-foreground">Not-paid loads are still imported. Untick any you want to skip.</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Detailed per-block report */}
                  <div className="rounded-lg border overflow-hidden">
                    <div className="px-3 py-2 bg-slate-100 dark:bg-slate-800 text-xs font-semibold flex justify-between items-center">
                      <span>Block-by-block report ({tripImportResult.block_report.length})</span>
                      {tripImportResult.dry_run && (
                        <span className="text-[10px] text-muted-foreground font-normal">
                          Tick the ✓ column next to any skipped row to include it in the import and enter its Gross amount.
                        </span>
                      )}
                    </div>
                    <div className="max-h-72 overflow-auto text-xs">
                      <table className="w-full min-w-[640px]">
                        <thead className="bg-slate-50 dark:bg-slate-900 sticky top-0 z-10">
                          <tr className="text-left text-[10px] uppercase text-muted-foreground">
                            <th className="px-2 py-1 w-[36px] text-center" title="Include this row in the import">
                              <span className="sr-only">Include</span>
                              ✓
                            </th>
                            <th className="px-2 py-1">Block</th>
                            <th className="px-2 py-1">Status / Unit</th>
                            <th className="px-2 py-1">Driver in file</th>
                            <th className="px-2 py-1">Loc</th>
                            <th className="px-2 py-1 text-right">Gross</th>
                            <th className="px-2 py-1">End</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {tripImportResult.block_report.filter((b) => tripKindFilter === 'both' || (tripKindFilter === 'trip' ? b.kind === 'trip' : b.kind !== 'trip')).map((b, i) => {
                            const isUnassigned = b.status === 'created_unassigned';
                            const isCreated = b.status === 'created';
                            const isSkipped = b.status === 'skipped';
                            const isForced = tripImportForceInclude.has(b.block_id);
                            const isExcluded = tripImportExclude.has(b.block_id);
                            // A row is "in the import" if it would be created (or force-included)
                            // AND the user hasn't opted it out.
                            const willImport = (isCreated || isUnassigned || (isSkipped && isForced)) && !isExcluded;
                            const statusColor = isCreated
                              ? 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30'
                              : isUnassigned
                                ? 'text-amber-700 bg-amber-50 dark:bg-amber-950/30'
                                : 'text-slate-600 bg-slate-50 dark:bg-slate-900';
                            const reasonLabel = {
                              assigned: '✓ assigned',
                              company_owned: '✓ company',
                              no_driver_match: 'no driver match',
                              driver_has_no_unit: 'driver has no unit',
                              outside_week: 'outside this week',
                              duplicate: 'duplicate',
                              cross_week_duplicate: 'already in another week',
                              no_pay: 'no pay',
                              no_end_datetime: 'no end date',
                              canceled: 'canceled by Amazon (not paid)',
                            }[b.reason] || b.reason;
                            // Gross is ALWAYS editable during preview — even when the file has a
                            // pay value. Users may need to correct/override amounts, and rows
                            // with no pay in the file (needs_payout) definitely need entry.
                            const canEditGross = tripImportResult.dry_run && willImport;
                            const currentPayout = tripImportPayoutOverrides[b.block_id];
                            const displayPay = currentPayout !== undefined && currentPayout !== ''
                              ? currentPayout
                              : (b.payout !== undefined && b.payout !== null ? String(b.payout) : '');
                            return (
                              <tr key={i} className={`hover:bg-slate-50 dark:hover:bg-slate-900/50 ${isForced ? 'bg-blue-50/40 dark:bg-blue-950/20' : ''} ${isExcluded ? 'opacity-50 line-through' : ''}`}>
                                <td className="px-2 py-1 text-center align-top">
                                  {tripImportResult.dry_run && (isCreated || isUnassigned) ? (
                                    <input
                                      type="checkbox"
                                      checked={!isExcluded}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setTripImportExclude((prev) => {
                                          const next = new Set(prev);
                                          if (checked) next.delete(b.block_id);
                                          else next.add(b.block_id);
                                          return next;
                                        });
                                      }}
                                      className="cursor-pointer accent-blue-600"
                                      data-testid={`preview-exclude-${b.block_id}`}
                                      title="Uncheck to skip this row on import"
                                    />
                                  ) : tripImportResult.dry_run && isSkipped && b.reason !== 'canceled' ? (
                                    <input
                                      type="checkbox"
                                      checked={isForced}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setTripImportForceInclude((prev) => {
                                          const next = new Set(prev);
                                          if (checked) next.add(b.block_id);
                                          else next.delete(b.block_id);
                                          return next;
                                        });
                                      }}
                                      className="cursor-pointer accent-blue-600"
                                      data-testid={`preview-force-${b.block_id}`}
                                      title="Include this skipped row"
                                    />
                                  ) : (
                                    <span className="text-emerald-600" title="Included by default">✓</span>
                                  )}
                                </td>
                                <td className="px-2 py-1 font-mono align-top">
                                  <div className="flex items-center gap-1.5">
                                    <span>{b.block_id}</span>
                                    {b.kind === 'trip' && (
                                      <span className="px-1.5 py-0 rounded text-[9px] font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" title="One-way trip (no Block ID)">TRIP</span>
                                    )}
                                  </div>
                                  {b.paid_status && <div className="mt-1"><PaidBadge b={b} /></div>}
                                </td>
                                <td className="px-2 py-1 align-top">
                                  {/* Inline assign dropdown when unassigned during preview */}
                                  {(isUnassigned || (isSkipped && isForced)) && tripImportResult.dry_run ? (
                                    <Select
                                      value={tripImportOverrides[b.block_id] || ''}
                                      onValueChange={(v) => setTripImportOverrides(prev => ({ ...prev, [b.block_id]: v }))}
                                    >
                                      <SelectTrigger
                                        className="h-7 text-xs w-[150px] border-amber-400 bg-amber-50 dark:bg-amber-950/30"
                                        data-testid={`preview-assign-${b.block_id}`}
                                      >
                                        <SelectValue placeholder="⚠ Assign…" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <UnitSelectItems units={units} hideInactive includeCompany />
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColor}`}>
                                      {b.unit_number ? `→ ${b.unit_number}` : reasonLabel}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-1 truncate max-w-[140px] align-top">{b.driver_in_file || b.driver || '—'}</td>
                                <td className="px-2 py-1 truncate max-w-[120px] align-top">{b.location || b.location_code || '—'}</td>
                                <td className="px-2 py-1 text-right font-mono align-top">
                                  {canEditGross ? (
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder={b.needs_payout ? '$ enter amount' : '$ override'}
                                      value={displayPay}
                                      onChange={(e) => setTripImportPayoutOverrides(prev => ({ ...prev, [b.block_id]: e.target.value }))}
                                      className={`h-7 w-[100px] text-right text-xs px-2 rounded border ${b.needs_payout ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                                      data-testid={`preview-payout-${b.block_id}`}
                                    />
                                  ) : (
                                    <span className={b.needs_payout ? 'text-amber-700 font-semibold' : ''}>
                                      ${b.payout?.toLocaleString() || 0}
                                      {b.needs_payout && <span className="ml-1 text-[9px] text-amber-700">⚠</span>}
                                    </span>
                                  )}
                                </td>
                                <td className="px-2 py-1 text-muted-foreground align-top whitespace-nowrap">{(b.end_date || b.end || '').slice(0, 10)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-2 pt-3">
                <Button variant="outline" onClick={closeTripImportDialog} data-testid="trip-import-close">
                  Close
                </Button>
                {!tripImportResult && (
                  <Button
                    onClick={() => handleTripImport(false)}
                    disabled={!tripImportFile || tripImporting}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="trip-import-preview"
                  >
                    {tripImporting ? 'Reading...' : 'Preview Import'}
                  </Button>
                )}
                {tripImportResult && tripImportResult.dry_run && (
                  <>
                    <Button
                      onClick={() => { setTripImportResult(null); setTripImportOverrides({}); setTripImportForceInclude(new Set()); setTripImportExclude(new Set()); }}
                      variant="outline"
                      data-testid="trip-import-back"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={() => handleTripImport(true)}
                      disabled={tripImporting || ((tripImportResult.created_count + tripImportResult.created_unassigned_count) === 0 && tripImportForceInclude.size === 0)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="trip-import-confirm"
                    >
                      {tripImporting ? 'Importing...' : (() => {
                        const excluded = tripImportExclude.size;
                        const total = tripImportResult.created_count + tripImportResult.created_unassigned_count - excluded;
                        const ovrCount = Object.values(tripImportOverrides).filter(Boolean).length;
                        const forcedCount = tripImportForceInclude.size;
                        const parts = [];
                        if (ovrCount > 0) parts.push(`${ovrCount} manually assigned`);
                        if (forcedCount > 0) parts.push(`+${forcedCount} force-included`);
                        if (excluded > 0) parts.push(`−${excluded} excluded`);
                        return parts.length > 0
                          ? `Confirm — Import ${Math.max(0, total + forcedCount)} (${parts.join(', ')})`
                          : `Confirm — Import ${Math.max(0, total + forcedCount)} Load${(total + forcedCount) !== 1 ? 's' : ''}`;
                      })()}
                    </Button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Bulk Delete Confirmation Dialog */}
          <Dialog open={bulkDeleteConfirmOpen} onOpenChange={setBulkDeleteConfirmOpen}>
            <DialogContent className="sm:max-w-[420px]">
              <DialogHeader>
                <DialogTitle className="font-manrope">Delete {selectedLoadIds.size} load{selectedLoadIds.size !== 1 ? 's' : ''}?</DialogTitle>
                <DialogDescription>
                  This will permanently delete the selected load{selectedLoadIds.size !== 1 ? 's' : ''}. This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setBulkDeleteConfirmOpen(false)} data-testid="bulk-delete-cancel">
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  data-testid="bulk-delete-confirm"
                >
                  {bulkDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          </>
          )}
        </div>
      </div>

      {/* Date Filter (Week Navigation OR Custom Range) */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <DateRangePicker
              mode={dateMode}
              onModeChange={setDateMode}
              weekStart={weekStart}
              onWeekStartChange={setWeekStart}
              startDate={rangeStart}
              endDate={rangeEnd}
              onRangeChange={(s, e) => { setRangeStart(s); setRangeEnd(e); }}
            />

            {/* Search + Unit Filter */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by block / route / broker / amount…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-[240px]"
                  data-testid="load-search-input"
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6"
                    onClick={() => setSearchQuery('')}
                    data-testid="load-search-clear"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Unit Filter */}
              <Select
                value={selectedUnit}
                onValueChange={setSelectedUnit}
              >
                <SelectTrigger className="w-[200px]" data-testid="unit-filter-select">
                  <SelectValue placeholder="Filter by unit" />
                </SelectTrigger>
                <SelectContent>
                  <UnitSelectItems units={units} includeAll allLabel="All Units" />
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search Results Summary */}
      {searchQuery && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="load-search-summary">
          <Search className="h-4 w-4" />
          Found {filteredLoads.length} load{filteredLoads.length !== 1 ? 's' : ''} matching "{searchQuery}"
        </div>
      )}

      {/* Week Summary */}
      {loads.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="loads-week-stats">
          <StatTile label="Gross Payout" value={formatCurrency(weekTotals.gross)} tone="primary" delay={60} testid="loads-stat-gross" className="col-span-2 lg:col-span-1 lg:order-2" />
          <StatTile label="Loads" value={filteredLoads.length} accent="#60a5fa" testid="loads-stat-count" className="lg:order-1" />
          <StatTile label="Dispatch Fees" value={`−${formatCurrency(weekTotals.fees)}`} accent="#93c5fd" delay={120} testid="loads-stat-fees" className="lg:order-3" />
        </div>
      )}

      {/* Bulk-action bar (when loads selected) */}
      <Tabs defaultValue="loads" className="w-full">
        <TabsList className="grid w-full sm:w-auto grid-cols-2 sm:inline-grid" data-testid="loads-tabs">
          <TabsTrigger value="loads" data-testid="tab-loads">
            Loads {loads.length > 0 ? `(${filteredLoads.length})` : ''}
          </TabsTrigger>
          <TabsTrigger value="additions" data-testid="tab-additions">
            Additions
          </TabsTrigger>
        </TabsList>
        <TabsContent value="loads" className="space-y-4 mt-4">
      {canEdit && selectedLoadIds.size > 0 && (
        <Card className="border-blue-300 bg-blue-50 dark:bg-blue-950/30" data-testid="bulk-action-bar">
          <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
              {selectedLoadIds.size} load{selectedLoadIds.size !== 1 ? 's' : ''} selected
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedLoadIds(new Set())}
                data-testid="clear-selection-btn"
              >
                Clear
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkDeleteConfirmOpen(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete Selected
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loads Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {canEdit && (
                    <TableHead className="w-[40px]">
                      <input
                        type="checkbox"
                        checked={filteredLoads.length > 0 && selectedLoadIds.size === filteredLoads.length}
                        onChange={toggleSelectAll}
                        className="cursor-pointer"
                        data-testid="select-all-loads"
                        aria-label="Select all loads"
                      />
                    </TableHead>
                  )}
                  <TableHead>Reference</TableHead>
                  <TableHead className="sticky left-0 z-20 bg-background sm:static sm:bg-transparent">Unit</TableHead>
                  <TableHead>Broker</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead className="text-right">Miles</TableHead>
                  <TableHead className="text-right">Payout</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                  <TableHead>Status</TableHead>
                  {canEdit && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLoads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 11 : 9} className="text-center py-8 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <Package className="h-8 w-8 text-muted-foreground/50" />
                        <p>{searchQuery ? `No loads match "${searchQuery}"` : 'No loads for this week. Add your first load!'}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLoads.map((load) => (
                    <TableRow key={load.id} data-testid={`load-row-${load.id}`} className={selectedLoadIds.has(load.id) ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''}>
                      {canEdit && (
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedLoadIds.has(load.id)}
                            onChange={() => toggleSelectLoad(load.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="cursor-pointer"
                            data-testid={`select-load-${load.id}`}
                            aria-label={`Select load ${load.load_reference}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium">{load.load_reference}</TableCell>
                      <TableCell className="sticky left-0 z-10 bg-background sm:static sm:bg-transparent">
                        {load.unit_id ? (
                          <div className="flex flex-col leading-tight">
                            <span className="font-semibold">{getUnitNumber(load.unit_id)}</span>
                            {load.load_reference && (
                              <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wide">
                                #{String(load.load_reference).slice(-4)}
                              </span>
                            )}
                          </div>
                        ) : canEdit ? (
                          <div className="flex flex-col gap-0.5">
                            <Select
                              value=""
                              onValueChange={(v) => handleAssignUnit(load.id, v)}
                            >
                              <SelectTrigger
                                className="h-7 text-xs border-blue-400 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 w-[140px]"
                                data-testid={`assign-unit-${load.id}`}
                              >
                                <SelectValue placeholder="Assign…" />
                              </SelectTrigger>
                              <SelectContent>
                                <UnitSelectItems units={units} hideInactive includeCompany />
                              </SelectContent>
                            </Select>
                            {load.load_reference && (
                              <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wide">
                                #{String(load.load_reference).slice(-4)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col leading-tight">
                            <span className="text-xs text-blue-700 dark:text-blue-300">Unassigned</span>
                            {load.load_reference && (
                              <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wide">
                                #{String(load.load_reference).slice(-4)}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{load.broker_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span className="truncate max-w-[100px]">{load.pickup_location}</span>
                          <span className="text-muted-foreground">→</span>
                          <span className="truncate max-w-[100px]">{load.delivery_location}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(load.start_date)} - {formatDate(load.end_date)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {load.miles ? `${load.miles.toLocaleString()} mi` : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-blue-700 dark:text-blue-300" data-testid={`load-gross-${load.id}`}>
                        {formatCurrency(load.payout_amount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-slate-600 dark:text-slate-300">
                        {(load.dispatch_fee || 0) === 0 ? formatCurrency(0) : `-${formatCurrency(load.dispatch_fee)}`}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[load.status]}>
                          {load.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(load)}
                              data-testid={`edit-load-${load.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(load.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-load-${load.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>
        <TabsContent value="additions" className="mt-4">
          <AdditionsPanel
            units={units}
            selectedUnit={selectedUnit}
            weekStart={weekStart}
            canEdit={canEdit}
          />
        </TabsContent>
      </Tabs>

      {/* PDF Viewer Dialog */}
      <Dialog open={pdfViewerOpen} onOpenChange={closePdfViewer}>
        <DialogContent className="max-w-4xl h-[90vh] p-0">
          <DialogHeader className="p-4 pb-2 border-b flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Settlement Pay - Unit {pdfUnitNumber}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadPdf}
                data-testid="pdf-download-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={closePdfViewer}
                data-testid="pdf-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 h-full p-4 pt-0">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-[calc(90vh-80px)] border rounded-lg"
                title="Settlement Pay PDF"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Date Range Dialog */}
      <Dialog open={dateRangeDialogOpen} onOpenChange={setDateRangeDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-blue-600" />
              Custom Date Range Invoice
            </DialogTitle>
            <DialogDescription>
              Generate an invoice for loads within a custom date range for Unit {getUnitNumber(selectedUnit)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invoice-start-date">Start Date</Label>
              <Input
                id="invoice-start-date"
                type="date"
                value={invoiceStartDate}
                onChange={(e) => setInvoiceStartDate(e.target.value)}
                data-testid="invoice-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoice-end-date">End Date</Label>
              <Input
                id="invoice-end-date"
                type="date"
                value={invoiceEndDate}
                onChange={(e) => setInvoiceEndDate(e.target.value)}
                data-testid="invoice-end-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDateRangeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleGenerateCustomInvoice}
              disabled={!invoiceStartDate || !invoiceEndDate}
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Override-or-reassign prompt */}
      <Dialog open={!!reassignPrompt} onOpenChange={(o) => !o && setReassignPrompt(null)}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Apply this change…</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              You changed the <strong className="capitalize">{reassignPrompt?.kind?.replace('_', ' ')}</strong> to <strong>{reassignPrompt?.name}</strong>.
              How should this be applied?
            </p>
            <div className="rounded-lg border p-3 bg-blue-50/40 dark:bg-blue-950/20 text-xs">
              <p><strong>Just this load</strong> — keep the unit&apos;s permanent assignment as-is. Use this if someone is covering a single load.</p>
            </div>
            <div className="rounded-lg border p-3 bg-amber-50/40 dark:bg-amber-950/20 text-xs">
              <p><strong>Reassign permanently</strong> — also update the unit so all future loads default to this {reassignPrompt?.kind?.replace('_', ' ')}. Affects the Units page and the People summaries.</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setReassignPrompt(null)}>Cancel</Button>
            <Button onClick={applyOverrideForLoadOnly} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-override-load-only">
              Just this load
            </Button>
            <Button onClick={applyReassignToUnit} className="bg-amber-600 hover:bg-amber-700" data-testid="apply-reassign-unit">
              Reassign permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Amazon Trips Import cross-week duplicate confirm — same friendly BLUE style */}
      <Dialog open={!!tripImportCrossWeekDups} onOpenChange={(o) => !o && setTripImportCrossWeekDups(null)}>
        <DialogContent className="sm:max-w-[720px] w-[94vw] max-h-[85vh] flex flex-col p-0" data-testid="trip-import-cross-week-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <AlertCircle className="h-4 w-4" /> Duplicate Loads Detected
            </DialogTitle>
            <DialogDescription className="text-sm">
              These loads already exist in another week. Skip them, or add them anyway?
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 divide-y divide-blue-100">
              {(tripImportCrossWeekDups || []).map((d) => (
                <div key={d.load_reference} className="p-3 text-sm" data-testid={`trip-import-dup-row-${d.load_reference}`}>
                  <div className="font-semibold text-slate-800 mb-1">Load {d.load_reference}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded bg-white/70 border border-slate-200 p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Already in system</div>
                      <div className="text-slate-700 mt-0.5">Week: <b>{d.existing?.week_start || '—'}</b></div>
                      <div className="text-slate-700">Broker: <b>{d.existing?.broker_name || '—'}</b></div>
                      <div className="text-slate-700">Amount: <b>${Number(d.existing?.payout_amount || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</b></div>
                    </div>
                    <div className="rounded bg-blue-100/70 border border-blue-200 p-2">
                      <div className="text-[10px] uppercase text-blue-700">Being imported</div>
                      <div className="text-slate-700 mt-0.5">Week: <b>{d.incoming?.week_start || '—'}</b></div>
                      <div className="text-slate-700">Amount: <b>${Number(d.incoming?.payout_amount || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</b></div>
                      <div className="text-slate-700">Start: <b>{d.incoming?.start_date ? new Date(d.incoming.start_date).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'}) : '—'}</b></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="p-3 border-t gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setTripImportCrossWeekDups(null)} data-testid="trip-import-dup-cancel">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => {
                setTripImportCrossWeekDups(null);
                setTripImportOpen(false);
                toast.success('Duplicate loads skipped. Import complete.');
                loadLoads();
              }}
              data-testid="trip-import-dup-skip-all"
            >
              Skip all duplicates
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                setTripImportCrossWeekDups(null);
                handleTripImport(true, { forceCrossWeek: true });
              }}
              data-testid="trip-import-dup-add-anyway"
            >
              Add all anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cross-week Amazon import duplicate confirm — themed BLUE (not red) */}
      <Dialog open={!!relayCrossWeekDups} onOpenChange={(o) => !o && setRelayCrossWeekDups(null)}>
        <DialogContent className="sm:max-w-[720px] w-[94vw] max-h-[85vh] flex flex-col p-0" data-testid="relay-cross-week-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <AlertCircle className="h-4 w-4" /> Duplicate Load IDs Detected
            </DialogTitle>
            <DialogDescription className="text-sm">
              These loads already exist in another week. Choose whether to add them again or skip them.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <div className="rounded-lg border border-blue-200 bg-blue-50/40 divide-y divide-blue-100">
              {(relayCrossWeekDups?.dups || []).map((d) => (
                <div key={d.load_reference} className="p-3 text-sm" data-testid={`relay-dup-row-${d.load_reference}`}>
                  <div className="font-semibold text-slate-800 mb-1">Load {d.load_reference}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded bg-white/70 border border-slate-200 p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Already in system</div>
                      <div className="text-slate-700 mt-0.5">Week: <b>{d.existing?.week_start || '—'}</b></div>
                      <div className="text-slate-700">Broker: <b>{d.existing?.broker_name || '—'}</b></div>
                      <div className="text-slate-700">Amount: <b>${Number(d.existing?.payout_amount || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</b></div>
                    </div>
                    <div className="rounded bg-blue-100/70 border border-blue-200 p-2">
                      <div className="text-[10px] uppercase text-blue-700">Being imported</div>
                      <div className="text-slate-700 mt-0.5">Week: <b>{d.incoming?.week_start || '—'}</b></div>
                      <div className="text-slate-700">Amount: <b>${Number(d.incoming?.payout_amount || 0).toLocaleString('en-US', {minimumFractionDigits:2})}</b></div>
                      <div className="text-slate-700">Start: <b>{d.incoming?.start_date || '—'}</b></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="p-3 border-t gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setRelayCrossWeekDups(null)} data-testid="relay-dup-cancel">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => {
                // Skip all duplicates — just proceed without them (they were already skipped server-side).
                setRelayCrossWeekDups(null);
                setRelayDialogOpen(false);
                resetRelayState();
                toast.success('Duplicate loads skipped. Import complete.');
                loadLoads();
              }}
              data-testid="relay-dup-skip-all"
            >
              Skip all duplicates
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                setRelayCrossWeekDups(null);
                handleRelayApply({ forceCrossWeek: true });
              }}
              data-testid="relay-dup-add-anyway"
            >
              Add all anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate load ID confirm — themed to site colors (blue), NOT alarm red */}
      <Dialog open={!!dupWarning} onOpenChange={(o) => !o && setDupWarning(null)}>
        <DialogContent className="sm:max-w-[520px] w-[94vw]" data-testid="dup-load-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <AlertCircle className="h-4 w-4" /> Load ID Already Exists
            </DialogTitle>
            <DialogDescription className="text-sm">
              This load ID was already added in a previous week. Would you like to add it again anyway?
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 text-sm space-y-1">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Existing week</span>
              <span className="font-medium tabular-nums">{dupWarning?.existing?.week_start || '—'}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Broker</span>
              <span className="font-medium truncate">{dupWarning?.existing?.broker_name || '—'}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Amount</span>
              <span className="font-medium tabular-nums">
                ${Number(dupWarning?.existing?.payout_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDupWarning(null)} data-testid="dup-load-cancel">
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={confirmDupCreate}
              data-testid="dup-load-confirm"
            >
              Add anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
